/**
 * Time ruler. Pointer down + drag scrubs the playhead.
 */

import { useRef } from 'react';
import { useVideoStore } from '@/stores/videoStore';

interface TimelineRulerProps {
  totalMs: number;
  pxPerSec: number;
  /** Track width in px (ruler spans the whole track, past the last scene). */
  widthPx: number;
}

/** Pick a label step that keeps labels at least ~60px apart. */
function labelStepSec(pxPerSec: number): number {
  const steps = [0.5, 1, 2, 5, 10, 15, 30, 60];
  return steps.find((s) => s * pxPerSec >= 60) ?? 60;
}

export default function TimelineRuler({ totalMs, pxPerSec, widthPx }: TimelineRulerProps) {
  const seek = useVideoStore((s) => s.seek);
  const pause = useVideoStore((s) => s.pause);
  const draggingRef = useRef(false);

  const step = labelStepSec(pxPerSec);
  const ticks: number[] = [];
  const maxSec = widthPx / pxPerSec;
  for (let t = 0; t <= maxSec + 1e-6; t += step) ticks.push(Number(t.toFixed(3)));

  const msAt = (e: React.PointerEvent<HTMLDivElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    return Math.min(totalMs, Math.max(0, (x / pxPerSec) * 1000));
  };

  return (
    <div
      className="vt-ruler"
      style={{ width: widthPx }}
      onPointerDown={(e) => {
        draggingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        pause();
        seek(msAt(e));
      }}
      onPointerMove={(e) => {
        if (draggingRef.current) seek(msAt(e));
      }}
      onPointerUp={(e) => {
        draggingRef.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
      onPointerCancel={() => {
        draggingRef.current = false;
      }}
    >
      {ticks.map((t) => (
        <span key={t} className="vt-ruler-tick" style={{ left: t * pxPerSec }}>
          {step < 1 && t % 1 !== 0 ? '' : `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, '0')}`}
        </span>
      ))}
    </div>
  );
}
